/**
 * ProtectedRoute component
 * Enforces authentication and subscription requirements
 */

import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from '@/hooks/useAuthState';
import { getOrganization } from '@/services/organization.service';
import { LoadingSpinner } from './LoadingSpinner';
import './styles/ProtectedRoute.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresPassword?: boolean;
  requiresSubscription?: boolean;
}

export function ProtectedRoute({
  children,
  requiresPassword = false,
  requiresSubscription = true,
}: ProtectedRouteProps) {
  const { user, loading, hasAccess } = useAuthState();
  const location = useLocation();
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);

  // Reset PIN verification when returning to this route
  useEffect(() => {
    if (requiresPassword) {
      setPinVerified(false);
      setPin('');
      setPinError('');
    }
  }, [location.pathname, requiresPassword]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to billing if no active subscription (only if subscription is required)
  if (requiresSubscription && !hasAccess) {
    return <Navigate to="/billing" replace />;
  }

  // Handle PIN verification for admin access
  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    setVerifying(true);

    try {
      const org = await getOrganization(user.uid);

      if (!org) {
        setPinError('Organization not found');
        setVerifying(false);
        return;
      }

      if (pin === org.adminPin) {
        setPinVerified(true);
      } else {
        setPinError('Incorrect PIN');
        setPin('');
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      setPinError('Failed to verify PIN');
    } finally {
      setVerifying(false);
    }
  };

  // Show PIN modal if password required and not yet verified
  if (requiresPassword && !pinVerified) {
    return (
      <div className="pin-modal-overlay">
        <div className="pin-modal-content">
          <h2 className="pin-modal-title">Admin Access 🔒</h2>
          <p className="pin-modal-subtitle">
            Enter your 5-digit PIN to continue
          </p>

          <form onSubmit={handlePinSubmit} className="pin-modal-form">
            <input
              type="text"
              value={pin}
              onChange={(e) =>
                setPin(e.target.value.replace(/\D/g, '').slice(0, 5))
              }
              maxLength={5}
              className="pin-input"
              placeholder="•••••"
              autoFocus
              disabled={verifying}
            />

            {pinError && <div className="pin-error">{pinError}</div>}

            <div className="pin-modal-buttons">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="pin-btn-secondary"
                disabled={verifying}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="pin-btn-primary"
                disabled={verifying || pin.length !== 5}
              >
                {verifying ? 'Verifying...' : 'Enter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
